import { useMemo } from 'react'
import Cookies from 'js-cookie'
import constants from '@constants'
import useData, { type ResponseState } from '../utils/use-data'
import fetcherFn from '../utils/fetcher'
import getCartId from '../utils/get-cart-id'
import { normalizeShopifyCart } from '../utils/normalize'
import { getCartQuery } from '../utils/queries'
import type { HookFetcherFn } from '../utils/types'
import type { MaybeCart, GetCartHook } from '../types/cart'

export type UseCart = (input?: {
  cartId?: string
}) => ResponseState<MaybeCart> & {
  isEmpty: boolean
}

export type CartFetcher = HookFetcherFn<GetCartHook>

const useCart: UseCart = (input = {}) => {
  const { SHOPIFY_CART_ID_COOKIE, SHOPIFY_COOKIE_EXPIRE, SITE_DOMAIN } =
    constants

  const hookFetcher: CartFetcher = async (hookFetcherContext) => {
    const { options, fetch } = hookFetcherContext
    const localCartId = hookFetcherContext.input.cartId
    const cartId = getCartId(localCartId)

    if (cartId) {
      const response = await fetch({
        ...options,
        variables: { cartId },
      })
      const { cart } = response

      if (!cart) {
        Cookies.remove(SHOPIFY_CART_ID_COOKIE, { domain: SITE_DOMAIN })
        return null
      } else {
        Cookies.set(SHOPIFY_CART_ID_COOKIE, cartId, {
          expires: SHOPIFY_COOKIE_EXPIRE,
          domain: SITE_DOMAIN,
        })
      }

      return normalizeShopifyCart(cart)
    }

    return null
  }

  const useDataOptions = {
    fetcher: hookFetcher,
    fetchOptions: { query: getCartQuery },
  }

  const response = useData(useDataOptions, input, fetcherFn)
  const memo = useMemo(
    () =>
      Object.create(response, {
        isEmpty: {
          get() {
            return (response.data?.lineItems.length ?? 0) <= 0
          },
          enumerable: true,
        },
      }),
    [response]
  )

  return memo
}

export default useCart
